import { emptyAdminApi as api } from "../emptyAdminApi";

export type SlowboatPassport = {
  id: string;
  title?: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  passport_no: string;
  passport_country?: string;
  passport_expiry_date?: string;
  group_label?: string;
  status?: string;
};

export type SlowboatPassportInput = Omit<SlowboatPassport, "id" | "status"> & {
  date_of_birth: string;
  passport_country: string;
};

export type SlowboatPassportList = {
  records: SlowboatPassport[];
  count: number;
};

export type SlowboatActivationRequest = {
  orderId: string;
  simIdx: number;
};

const slowboatAdminApi = api
  .enhanceEndpoints({
    addTagTypes: ["SlowboatPassports"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getSlowboatPassports: build.query<SlowboatPassportList, void>({
        query: () => "/slowboat/passports?limit=100",
        providesTags: ["SlowboatPassports"],
      }),
      importSlowboatPassports: build.mutation<
        { created: number; skipped_duplicates: number },
        { records: SlowboatPassportInput[]; groupLabel?: string }
      >({
        query: ({ records, groupLabel }) => ({
          url: "/slowboat/passports",
          method: "POST",
          body: {
            records: records.map(({ group_label, ...record }) => record),
            ...(groupLabel ? { group_label: groupLabel } : {}),
          },
        }),
        invalidatesTags: ["SlowboatPassports"],
      }),
      updateSlowboatPassport: build.mutation<
        SlowboatPassport,
        { id: string; changes: Partial<SlowboatPassportInput> }
      >({
        query: ({ id, changes }) => ({
          url: `/slowboat/passports/${encodeURIComponent(id)}`,
          method: "PATCH",
          body: changes,
        }),
        invalidatesTags: ["SlowboatPassports"],
      }),
      withdrawSlowboatPassport: build.mutation<void, string>({
        query: (id) => ({
          url: `/slowboat/passports/${encodeURIComponent(id)}`,
          method: "DELETE",
        }),
        invalidatesTags: ["SlowboatPassports"],
      }),
      cancelSlowboatActivation: build.mutation<
        { message: string },
        SlowboatActivationRequest & { reason?: string }
      >({
        query: (body) => ({
          url: "/slowboat/activations/cancel",
          method: "POST",
          body,
        }),
      }),
      rescheduleSlowboatActivation: build.mutation<
        { message: string },
        SlowboatActivationRequest & { arrivalDate: string }
      >({
        query: (body) => ({
          url: "/slowboat/activations/reschedule",
          method: "POST",
          body,
        }),
      }),
    }),
  });

export const {
  useGetSlowboatPassportsQuery,
  useImportSlowboatPassportsMutation,
  useUpdateSlowboatPassportMutation,
  useWithdrawSlowboatPassportMutation,
  useCancelSlowboatActivationMutation,
  useRescheduleSlowboatActivationMutation,
} = slowboatAdminApi;
